import { useMemo, useState } from "react";
import Modal from "./Modal";
import type { Deal } from "../../../lib/types";
import { rescheduleEvent } from "../../../lib/dispatchActions";
import { useAuth } from "../../../context/AuthContext";

type EventType = "pre_inspection" | "adjuster_meeting" | "reinspection_adjuster" | "build";

interface Props {
  open: boolean;
  deal: Deal;
  type: EventType;
  onClose: () => void;
  onCompleted: () => void;
}

function isoDatePart(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function isoTimePart(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(11, 16); // HH:mm
}

export default function RescheduleModal({
  open,
  deal,
  type,
  onClose,
  onCompleted,
}: Props) {
  const { member } = useAuth();
  const current = useMemo(() => {
    if (type === "pre_inspection") return deal.pre_inspection_date;
    if (type === "adjuster_meeting") return deal.adjuster_meeting_date;
    if (type === "reinspection_adjuster") return deal.reinspection_adjuster_meeting_date;
    return deal.build_scheduled_date;
  }, [deal, type]);

  const [date, setDate] = useState(isoDatePart(current));
  const [time, setTime] = useState(
    type === "build" ? "" : isoTimePart(current),
  );
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const titleByType: Record<EventType, string> = {
    pre_inspection: "Reschedule pre-inspection",
    adjuster_meeting: "Reschedule adjuster meeting",
    reinspection_adjuster: "Reschedule 2nd adjuster meeting",
    build: "Reschedule build",
  };

  async function submit() {
    setErr(null);
    if (!date) return setErr("Pick a new date.");
    if (!reason.trim()) return setErr("Reason is required.");
    if (type !== "build" && !time)
      return setErr("Pick a new time.");
    setSubmitting(true);
    try {
      await rescheduleEvent(
        deal,
        type,
        {
          newDate: date,
          newTime: type === "build" ? null : time,
          reason: reason.trim(),
        },
        member?.id ?? null,
      );
      onCompleted();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      title={titleByType[type]}
      subtitle={`${deal.homeowner_name ?? "(no name)"} · ${deal.property_street ?? ""}`}
      onClose={onClose}
      footer={
        <>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 rounded text-sm text-slate-600 hover:text-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-bardin-orange text-white text-sm disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Reschedule"}
          </button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            New date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          />
        </div>
        {type !== "build" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              New time
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Reason
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
          placeholder={
            type === "build"
              ? "Weather, crew availability, homeowner request..."
              : "Why is this being moved?"
          }
        />
      </div>

      <p className="text-xs text-slate-500">
        Homeowner, RM, and calendar will be notified automatically.
      </p>

      {err && (
        <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>
      )}
    </Modal>
  );
}
